import { useState, type ReactNode } from "react";
import { buttonClasses } from "@dearbill/shared";
import { ReferSomeoneModal, type ReferEstimateContext } from "../../shell/ReferSomeoneModal";
import personIcon from "../../assets/icon-person.svg";

export interface HandoffSectionProps {
  orgId: string;
  /**
   * The current estimate's inputs, present only when a live estimate is on
   * screen. Passed to the modal so an email send attaches the cost-report PDF;
   * absent means the plain link. It carries only the plan and drugs, never a
   * patient field, so the referral stays anonymous.
   */
  estimateContext?: ReferEstimateContext | undefined;
}

/**
 * The form screen's handoff. It always shows, because the partner's job ends
 * here whatever the questions said; even "can't find the plan" refers fine.
 * The refer button opens the shared `ReferSomeoneModal`, which owns both
 * referral links. The result screen carries its own actions row (refer, print,
 * check another patient); this card is the path that skips the answer.
 *
 * When a live estimate is on screen, `estimateContext` rides to the modal so an
 * email send attaches the anonymous cost-report PDF; the referral URLs still
 * carry no plan or drug data.
 */
export function HandoffSection({ orgId, estimateContext }: HandoffSectionProps): ReactNode {
  const [referOpen, setReferOpen] = useState(false);

  return (
    <section
      aria-label="Refer the patient to DearBill"
      className="border-border-strong rounded-2xl border bg-white p-6 print:hidden"
    >
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => setReferOpen(true)}
          className={buttonClasses("primary", "lg", "gap-2")}
        >
          <img src={personIcon} alt="" className="size-4 shrink-0" />
          Refer patient to DearBill
        </button>
      </div>

      <ReferSomeoneModal
        open={referOpen}
        onClose={() => setReferOpen(false)}
        orgId={orgId}
        estimateContext={estimateContext}
      />
    </section>
  );
}
